import { useTranslation } from 'react-i18next'

import {
  Alert,
  DangerButton,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { WithdrawNoBidWindowNote } from '~/pages/SpotBidding/components/WithdrawNoBidWindowNote'

type Props = {
  /**
   * 取下対象のサマリ (1 行 1 要素)。 undefined ならモーダルは閉じている。
   * 通常入札は札 1 枚、 ブロック入札はグループの leg ごとに行を作る。
   */
  summaryLines: readonly string[] | undefined
  /** 入札締切 (= 受渡日前日 10:00 JST)。 残り時間の注記に使う。 */
  deadline: Date
  /** 残り時間の基準時刻 (= 現在時刻)。 test から固定できるよう prop で受ける。 */
  now: Date
  isSubmitting: boolean
  errorMessage: string | undefined
  onClose: () => void
  onConfirm: () => void
}

/**
 * 入札中の札の取下確認モーダル (AC-BSP-057)。 通常入札・ブロック入札で共有する。
 *
 * 対象札のサマリに加えて、 **札なし窓の注記**を必ず出す: 取消が成立してから
 * 再入札するまでの間そのコマに札が無い状態になり、 承認モードによっては再入札の
 * 承認完了までその状態が続く。 取消と再入札を内部で自動連結しない設計
 * (AC-BSP-081) の裏返しで、 「取消したまま締切を迎えて札なしになる」 事故を防ぐ
 * のがこの注記の役目。
 *
 * 削除確認 (`DeleteBidConfirmModal`) と同じ構成にしているのは、 取下も「元に
 * 戻せない撤回」 だから (design HTML に取下モーダルの surface は無く、 既存の
 * 撤回系モーダルに揃えている。 見た目の最終判断は Des 確認事項)。
 */
export const WithdrawBidConfirmModal = ({
  summaryLines,
  deadline,
  now,
  isSubmitting,
  errorMessage,
  onClose,
  onConfirm,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Modal
      footer={
        <>
          <TertiaryButton marginRight={3} onClick={onClose}>
            {t('page:biddingSpot.withdrawConfirm.cancel')}
          </TertiaryButton>
          <DangerButton loading={isSubmitting} onClick={onConfirm}>
            {t('page:biddingSpot.withdrawConfirm.confirm')}
          </DangerButton>
        </>
      }
      isOpen={summaryLines !== undefined}
      onClose={onClose}
      size="sm"
      title={t('page:biddingSpot.withdrawConfirm.title')}
    >
      <VStack align="stretch" gap={3}>
        {errorMessage !== undefined && (
          <Alert status="error" title={errorMessage} />
        )}
        {summaryLines !== undefined && summaryLines.length > 0 && (
          <VStack align="stretch" gap={1}>
            {summaryLines.map((line) => (
              <Text key={line}>{line}</Text>
            ))}
          </VStack>
        )}
        <WithdrawNoBidWindowNote deadline={deadline} now={now} />
      </VStack>
    </Modal>
  )
}
